/**
 * lib/supabaseMappers.ts
 * Centralized mapping utilities for Supabase data
 *
 * Architecture: WU1.5 - Supabase Integration Architecture Fix
 *
 * Purpose: Convert between database snake_case and TypeScript camelCase
 * at the repository boundary. This keeps the rest of the application
 * using idiomatic camelCase while respecting PostgreSQL conventions.
 *
 * ========================================================================
 * PURITY GUARANTEE
 * ========================================================================
 *
 * **Mappers never mutate input objects.**
 *
 * All mapping functions return new objects, leaving the original unchanged.
 * This allows repositories to safely reuse partial update objects without
 * side effects.
 *
 * ========================================================================
 * EDGE-CASE POLICIES (Handle once, reuse forever)
 * ========================================================================
 *
 * 1. **Dates**: Keep as ISO strings in domain layer
 *    - PostgreSQL: TIMESTAMPTZ → ISO string
 *    - Domain: string (ISO 8601 format)
 *    - Rationale: Consistent with JSON serialization, no timezone issues
 *
 * 2. **Numeric/Bigint**: Keep as strings to avoid precision loss
 *    - PostgreSQL: NUMERIC, BIGINT → string
 *    - Domain: string or number (depending on use case)
 *    - Rationale: JavaScript numbers lose precision for large integers
 *
 * 3. **JSONB Passthrough**: Don't deep-camelize already-camel JSON
 *    - Use allowlist for fields that contain pre-camelCased JSON
 *    - Example: If a JSONB column stores camelCase data, skip conversion
 *    - Current: No JSONB fields with camelCase content
 *
 * 4. **Partial Updates**: Strip `undefined` to prevent NULLing fields
 *    - Problem: { field: undefined } in update becomes NULL in database
 *    - Solution: `toPatch()` strips undefined values before conversion
 *    - Rationale: Partial updates should only affect provided fields
 *
 * ========================================================================
 * USAGE PATTERNS
 * ========================================================================
 *
 * **In Repositories (ONLY place these are used):**
 *
 * ```typescript
 * // READ: Convert snake_case → camelCase
 * const { data, error } = await supabase
 *   .from('users')
 *   .select('*')
 *   .eq('id', userId)
 *   .single();
 * if (error) throw error;
 * return toDomain<User>(data);
 *
 * // INSERT: Convert camelCase → snake_case
 * const wire = toWire(user);
 * const { data, error } = await supabase
 *   .from('users')
 *   .insert(wire)
 *   .select('*')
 *   .single();
 * if (error) throw error;
 * return toDomain<User>(data);
 *
 * // UPDATE (full): Convert camelCase → snake_case
 * const wire = toWire(user);
 * const { data, error } = await supabase
 *   .from('users')
 *   .update(wire)
 *   .eq('id', userId)
 *   .select('*')
 *   .single();
 * if (error) throw error;
 * return toDomain<User>(data);
 *
 * // UPDATE (partial): Strip undefined, then convert
 * const wire = toPatch(partialUser);
 * const { data, error } = await supabase
 *   .from('users')
 *   .update(wire)
 *   .eq('id', userId)
 *   .select('*')
 *   .single();
 * if (error) throw error;
 * return toDomain<User>(data);
 * ```
 *
 * ========================================================================
 * IMPORTANT RULES
 * ========================================================================
 *
 * 1. ❌ NEVER use these utilities outside `/repositories` folder
 * 2. ❌ NEVER use snake_case in Services or Components
 * 3. ✅ ALWAYS return camelCase domain types from repositories
 * 4. ✅ ALWAYS use `toPatch()` for partial updates (not `toWire()`)
 *
 * ========================================================================
 */

import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

/**
 * JSONB Fields Allowlist
 *
 * Table columns that contain pre-camelCased JSONB data and should NOT
 * be deep-converted by the mappers.
 *
 * Format: { tableName: ['column_name'] }
 *
 * Currently: No JSONB fields with camelCase content.
 * Add entries here as needed when JSONB columns store pre-camelCased data.
 *
 * Example:
 *   const JSONB_PASSTHROUGH_FIELDS: Record<string, string[]> = {
 *     users: ['preferences'], // preferences JSONB already camelCase
 *     audit_logs: ['metadata'] // metadata JSONB already camelCase
 *   };
 */
const JSONB_PASSTHROUGH_FIELDS: Record<string, string[]> = {
  // Empty - no JSONB passthrough fields yet
  // Add entries here as schema evolves
};

/**
 * Check if a field should skip deep conversion
 *
 * @param tableName - Database table name (snake_case)
 * @param fieldName - Field name (snake_case)
 * @returns true if field should be passed through without deep conversion
 */
export function isJsonbPassthrough(tableName: string, fieldName: string): boolean {
  const fields = JSONB_PASSTHROUGH_FIELDS[tableName];
  return fields ? fields.includes(fieldName) : false;
}

/**
 * Strip undefined values from an object
 *
 * Purpose: Prevent `undefined` from becoming NULL in database updates.
 * Partial updates should only affect fields that are explicitly provided.
 *
 * @param obj - Object potentially containing undefined values
 * @returns New object with undefined values removed
 *
 * @example
 * stripUndefined({ name: 'John', age: undefined })
 * // Returns: { name: 'John' }
 */
export function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Result object built dynamically, typed via return
  const result: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Convert database row (snake_case) to domain type (camelCase)
 *
 * Use this for ALL reads from Supabase (SELECT queries).
 * Converts deeply nested objects to handle JSONB columns.
 *
 * **Type Assertion Rationale**:
 * Domain types (camelCase) and wire types (snake_case) are structurally incompatible
 * due to different property names. The runtime conversion via camelcase-keys is correct,
 * but TypeScript cannot verify this at compile time. We use `unknown` to be explicit
 * about the boundary, then assert to the target domain type.
 *
 * **Guarantees**:
 * - ✅ Never mutates input (returns new object)
 * - ✅ Preserves null values (null → null)
 * - ✅ Handles nested objects (deep conversion)
 *
 * @param row - Database row with snake_case keys (unknown shape)
 * @returns Domain object with camelCase keys (new object, input unchanged)
 *
 * @example
 * const row = { user_id: '123', created_at: '2025-01-20' };
 * const domain = toDomain<User>(row);
 * // Returns: { userId: '123', createdAt: '2025-01-20' }
 */
export function toDomain<TDomain>(row: unknown): TDomain {
  if (row === null || row === undefined) {
    return row as TDomain;
  }

  // Future: Add runtime validation here if needed
  // const validated = DomainSchema.parse(camelized);
  // return validated as TDomain;

  return camelcaseKeys(row as Record<string, unknown>, { deep: true }) as unknown as TDomain;
}

/**
 * Convert array of database rows (snake_case) to array of domain types (camelCase)
 *
 * Convenience wrapper for mapping arrays of rows. Each row is independently converted
 * without mutating the input array or its elements.
 *
 * @param rows - Array of database rows with snake_case keys
 * @returns Array of domain objects with camelCase keys
 *
 * @example
 * const rows = [{ user_id: '1', full_name: 'Alice' }, { user_id: '2', full_name: 'Bob' }];
 * const users = toDomainArray<User>(rows);
 * // Returns: [{ userId: '1', fullName: 'Alice' }, { userId: '2', fullName: 'Bob' }]
 */
export function toDomainArray<TDomain>(rows: unknown[]): TDomain[] {
  return rows.map(row => toDomain<TDomain>(row));
}

/**
 * Convert domain type (camelCase) to database row (snake_case)
 *
 * Use this for INSERT operations and full UPDATE operations.
 * For partial updates, use `toPatch()` instead.
 *
 * **Type Assertion Rationale**:
 * Domain types (camelCase) and wire types (snake_case) are structurally incompatible
 * due to different property names. The runtime conversion via snakecase-keys is correct,
 * but TypeScript cannot verify this at compile time. We use `unknown` to be explicit
 * about the boundary, then assert to the target wire type.
 *
 * **Guarantees**:
 * - ✅ Never mutates input (returns new object)
 * - ✅ Preserves null values (null → null)
 * - ✅ Handles nested objects (deep conversion)
 *
 * @param obj - Domain object with camelCase keys
 * @returns Database row with snake_case keys (new object, input unchanged)
 *
 * @example
 * const user = { userId: '123', createdAt: '2025-01-20' };
 * const row = toWire<UserInsertRow>(user);
 * // Returns: { user_id: '123', created_at: '2025-01-20' }
 */
export function toWire<TWire>(obj: object): TWire {
  if (obj === null || obj === undefined) {
    return obj as TWire;
  }
  return snakecaseKeys(obj as Record<string, unknown>, { deep: true }) as unknown as TWire;
}

/**
 * Convert array of domain objects (camelCase) to array of database rows (snake_case)
 *
 * Convenience wrapper for mapping arrays of domain objects. Each object is independently
 * converted without mutating the input array or its elements.
 *
 * @param objs - Array of domain objects with camelCase keys
 * @returns Array of database rows with snake_case keys
 *
 * @example
 * const users = [{ userId: '1', fullName: 'Alice' }, { userId: '2', fullName: 'Bob' }];
 * const rows = toWireArray<UserInsertRow>(users);
 * // Returns: [{ user_id: '1', full_name: 'Alice' }, { user_id: '2', full_name: 'Bob' }]
 */
export function toWireArray<TWire>(objs: object[]): TWire[] {
  return objs.map(obj => toWire<TWire>(obj));
}

/**
 * Convert partial domain update (camelCase) to database update (snake_case)
 *
 * Use this for PATCH/UPDATE operations with partial data.
 * Strips undefined values BEFORE conversion to prevent NULLing fields.
 *
 * **IMPORTANT**: Always use `toPatch()` for partial updates, never `toWire()`.
 * The `undefined` vs `null` distinction is critical for correct update semantics.
 *
 * **Update Semantics**:
 * - `undefined` values are stripped (field not included in update, leaves DB value unchanged)
 * - `null` values are preserved (field will be explicitly set to NULL in database)
 * - Only provided fields are included in the update query
 *
 * **Type Assertion Rationale**:
 * Domain types (camelCase) and wire types (snake_case) are structurally incompatible
 * due to different property names. The runtime conversion via snakecase-keys is correct,
 * but TypeScript cannot verify this at compile time. We use `unknown` to be explicit
 * about the boundary, then assert to the target wire type.
 *
 * **Guarantees**:
 * - ✅ Never mutates input (returns new object)
 * - ✅ Strips undefined (preserves database values)
 * - ✅ Preserves null (explicitly clears fields)
 * - ✅ Handles nested objects (deep conversion)
 *
 * @param obj - Partial domain object with camelCase keys
 * @returns Database row with snake_case keys (undefined values removed, null preserved)
 *
 * @example
 * // Updating only specific fields
 * const update = { fullName: 'John', bio: null, age: undefined };
 * const row = toPatch<UserUpdateRow>(update);
 * // Returns: { full_name: 'John', bio: null }
 * // Note: `age` stripped (DB value unchanged), `bio` = null (cleared in DB)
 */
export function toPatch<TWire>(obj: Record<string, unknown>): TWire {
  if (obj === null || obj === undefined) {
    return obj as TWire;
  }
  // stripUndefined preserves null values per documented policy
  const cleaned = stripUndefined(obj);
  return snakecaseKeys(cleaned, { deep: true }) as unknown as TWire;
}

/**
 * Re-export Database type for convenience in repositories
 */
export type { Database } from '@/types/supabase.types';

/**
 * Type helper for database rows
 *
 * Use this to type database responses before converting to domain types.
 *
 * @example
 * type UserRow = DbRow<Database['public']['Tables']['users']['Row']>;
 */
export type DbRow<T> = T;

/**
 * Type helper for domain types
 *
 * Use this to document that a type is in camelCase (domain layer).
 *
 * @example
 * type User = DomainType<UserRow>;  // Documents conversion from row
 */
export type DomainType<T> = T;

// ============================================================================
// REPOSITORY HELPER UTILITIES
// ============================================================================

/**
 * Assert non-null for required joins
 *
 * PostgREST nested selects behave like LEFT JOINs, so joined data may be null
 * if the foreign key is invalid or missing. Use this helper to enforce that
 * required joins are present.
 *
 * @param value - Value to check for null/undefined
 * @param message - Error message if value is null
 * @returns The value, guaranteed to be non-null
 * @throws Error if value is null or undefined
 *
 * @example
 * const app = assertNotNull(row.beta_applications, 'Missing joined application');
 */
export function assertNotNull<T>(value: T | null | undefined, message: string): T {
  if (value == null) {
    throw new Error(message);
  }
  return value;
}

/**
 * Throw if Supabase error
 *
 * Cleaner error handling for repository methods. Throws the Supabase error
 * if present, otherwise returns the data.
 *
 * @param result - Supabase query result with data and error
 * @returns The data from the result
 * @throws The error if present
 *
 * @example
 * const data = throwIfError(await supabase.from('users').select('*'));
 */
export function throwIfError<T>(result: { data: T; error: Error | null }): T {
  if (result.error) {
    throw result.error;
  }
  return result.data;
}
